import { useEffect, useRef, useState } from 'react';
import { AudioLines, Trash2 } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  ConfirmDialog,
  Card,
  CardHead,
  EmptyState,
  FilePicker,
  Pill,
  PlayButton,
  SkeletonRows,
  useAudioPreview,
  type AudioPreview,
} from '../../components/ui';
import {
  describeVoiceStatus,
  useCharacterVoices,
  useCloneVoice,
  useCreateVoice,
  useCurrentUser,
  useDeleteVoice,
  useUpdateVoice,
  useVoiceSample,
} from '../../hooks';
import type { Character, Voice } from '../../types/api';
import styles from './Characters.module.css';

interface Props {
  character: Character;
  canEdit: boolean;
}

export function CharacterVoices({ character, canEdit }: Props) {
  const voices = useCharacterVoices(character.id);
  const preview = useAudioPreview();
  const createVoice = useCreateVoice();

  const items = voices.data?.items ?? [];

  const onPickSample = (file: File | null) => {
    if (!file) return;
    createVoice.mutate({
      character_id: character.id,
      sample: file,
      name: file.name.replace(/\.[^.]+$/, ''),
      make_current: items.length === 0,
    });
  };

  return (
    <Card>
      <CardHead
        title={<span className="eyebrow">голоса персонажа</span>}
        hint="один основной, остальные под язык"
        actions={
          <span className={styles.headMeta}>
            <span className={styles.rowMeta}>
              {items.length > 0 ? `${items.length} голос.` : 'нет голосов'}
            </span>
            {canEdit && (
              <FilePicker
                file={null}
                onPick={onPickSample}
                accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
                disabled={createVoice.isPending}
                buttonLabel={createVoice.isPending ? 'Загружаем образец…' : 'Добавить голос'}
                emptyLabel=""
                ariaLabel="Образец нового голоса"
              />
            )}
          </span>
        }
      />

      {createVoice.error && (
        <div className={styles.panelBody}>
          <ErrorBanner error={createVoice.error} />
        </div>
      )}
      {voices.error && (
        <div className={styles.panelBody}>
          <ErrorBanner error={voices.error} onRetry={() => void voices.refetch()} />
        </div>
      )}

      {voices.isLoading && (
        <div className={styles.panelBody}>
          <SkeletonRows rows={2} />
        </div>
      )}

      {!voices.isLoading && !voices.error && items.length === 0 && (
        <EmptyState
          compact
          icon={<AudioLines size={17} strokeWidth={1.5} />}
          title="Голоса нет"
          description="Загрузите запись голоса. Файл сохранится сразу, а сам голос создаётся отдельной кнопкой."
        />
      )}

      {items.map((voice) => (
        <VoiceRow key={voice.id} voice={voice} preview={preview} canEdit={canEdit} />
      ))}
    </Card>
  );
}

interface RowProps {
  voice: Voice;
  preview: AudioPreview;
  canEdit: boolean;
}

function VoiceRow({ voice, preview, canEdit }: RowProps) {
  const { canChange } = useCurrentUser();
  const own = canEdit && canChange(voice);
  const [wantsSample, setWantsSample] = useState(false);
  const sample = useVoiceSample(voice.id, wantsSample);
  const pendingPlay = useRef(false);
  const update = useUpdateVoice(voice.id);
  const remove = useDeleteVoice();
  const [confirming, setConfirming] = useState(false);
  const { clone, task } = useCloneVoice();

  const playing = preview.playingKey === voice.id;

  useEffect(() => {
    if (!pendingPlay.current || !sample.data) return;
    pendingPlay.current = false;
    preview.toggle(voice.id, sample.data.url);
  }, [sample.data, preview, voice.id]);

  const onPlay = () => {
    if (playing) {
      preview.stop();
      return;
    }
    if (sample.data) {
      preview.toggle(voice.id, sample.data.url);
      return;
    }
    pendingPlay.current = true;
    setWantsSample(true);
  };

  const cloning = clone.isPending || task.isRunning;
  const statusLabel = voice.status === 'ready' ? '' : describeVoiceStatus(voice);

  return (
    <div className={styles.row}>
      <PlayButton
        playing={playing}
        loading={sample.isFetching}
        disabled={sample.isError}
        onClick={onPlay}
        title={sample.isError ? 'Образец не найден' : playing ? 'Стоп' : 'Послушать образец'}
        ariaLabel={playing ? 'Остановить' : `Послушать образец: ${voice.name}`}
      />

      <span className={styles.rowName} title={voice.name}>
        {voice.name}
      </span>
      <Pill variant={voice.is_current ? 'solid' : 'soft'}>
        {voice.is_current ? 'основной' : 'запасной'}
      </Pill>
      {statusLabel && <Pill tone="warn">{statusLabel}</Pill>}

      <div className={styles.rowRight}>
        <span className={styles.rowMeta}>{voice.provider}</span>

        {own && !voice.is_current && voice.usable && (
          <Button
            size="xs"
            onClick={() => update.mutate({ is_current: true })}
            disabled={update.isPending}
            title="Этим голосом персонаж будет озвучен по умолчанию"
          >
            {update.isPending ? 'Меняем…' : 'Сделать основным'}
          </Button>
        )}

        {own && !voice.usable && (
          <Button
            size="xs"
            onClick={() => clone.mutate(voice.id)}
            disabled={cloning}
            title="Создать голос"
          >
            {cloning ? 'Клонируем…' : voice.external_voice_id ? 'Переклонировать' : 'Клонировать'}
          </Button>
        )}

        {own && (
          <Button
            size="xs"
            iconOnly
            variant="ghost"
            title="Удалить голос"
            aria-label="Удалить голос"
            disabled={remove.isPending}
            onClick={() => setConfirming(true)}
          >
            <Trash2 size={13} strokeWidth={1.6} />
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        destructive
        busy={remove.isPending}
        title="Удалить голос?"
        body="Голос удалится вместе с записью. Вернуть будет нельзя."
        confirmLabel="Удалить"
        onCancel={() => setConfirming(false)}
        onConfirm={() => remove.mutate(voice.id, { onSuccess: () => setConfirming(false) })}
      />
    </div>
  );
}
